import React, { useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, AlertCircle, Volume2, Sparkles, Monitor } from 'lucide-react';
import { Scene } from '../types/index.js';
import { formatMediaUrl } from '../utils/media.js';

export interface SceneSummary extends Partial<Scene> {
  id: string;
  order: number;
  sceneNumber: number;
  slideNumber: number;
  imageUrl?: string;
  imagePath?: string;
  narration?: string;
  text?: string;
  validationStatus?: 'valid' | 'warning' | 'invalid';
  isCustomized?: boolean;
  audioReady?: boolean;
  duration?: number;
}

export interface SceneCarouselProps {
  scenes: SceneSummary[];
  selectedSceneId: string;
  onSelectScene: (sceneId: string) => void;
  onReorder?: (orderedSceneIds: string[]) => void;
  readonly?: boolean;
}

export const SceneCarousel: React.FC<SceneCarouselProps> = ({
  scenes,
  selectedSceneId,
  onSelectScene,
  readonly = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeItemRef = useRef<HTMLButtonElement>(null);

  const currentIndex = scenes.findIndex((s) => s.id === selectedSceneId);
  const activeScene = currentIndex >= 0 ? scenes[currentIndex] : scenes[0];

  // Auto-scroll to keep active scene visible
  useEffect(() => {
    if (activeItemRef.current) {
      activeItemRef.current.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [selectedSceneId]);

  // Keyboard navigation (ArrowLeft & ArrowRight) when not typing in form controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, scenes]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      onSelectScene(scenes[currentIndex - 1].id);
    }
  };

  const handleNext = () => {
    if (currentIndex < scenes.length - 1) {
      onSelectScene(scenes[currentIndex + 1].id);
    }
  };

  if (!scenes || scenes.length === 0) {
    return null;
  }

  return (
    <div className="w-full bg-surface border border-bdr rounded-xl p-4 shadow-sm select-none">
      {/* Top Navigation Controls */}
      <div className="flex items-center justify-between mb-3 px-1">
        <button
          type="button"
          onClick={handlePrevious}
          disabled={currentIndex <= 0}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-txt-primary hover:bg-surface-secondary disabled:opacity-35 disabled:cursor-not-allowed transition-colors"
          title="Cena anterior (Seta esquerda)"
        >
          <ChevronLeft className="w-4 h-4 text-brand-primary" />
          <span>Anterior</span>
        </button>

        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-txt-primary">
            Cena {currentIndex >= 0 ? currentIndex + 1 : 1} de {scenes.length}
          </span>
          <span className="text-xs text-txt-muted bg-surface-secondary px-2 py-0.5 rounded-full border border-bdr">
            {currentIndex >= 0 ? `${currentIndex + 1} / ${scenes.length}` : ''}
          </span>
        </div>

        <button
          type="button"
          onClick={handleNext}
          disabled={currentIndex >= scenes.length - 1}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-txt-primary hover:bg-surface-secondary disabled:opacity-35 disabled:cursor-not-allowed transition-colors"
          title="Próxima cena (Seta direita)"
        >
          <span>Próxima</span>
          <ChevronRight className="w-4 h-4 text-brand-primary" />
        </button>
      </div>

      {/* Horizontal Carousel Thumbnails */}
      <div
        ref={containerRef}
        className="flex items-center gap-3 overflow-x-auto py-2 px-1 scrollbar-thin scrollbar-thumb-gray-200 hover:scrollbar-thumb-gray-300"
        style={{ scrollbarWidth: 'thin' }}
      >
        {scenes.map((scene, idx) => {
          const isSelected = scene.id === selectedSceneId;
          const displayNum = scene.sceneNumber || idx + 1;
          const hasCustomOverrides =
            scene.isCustomized ?? (scene.overrides && Object.keys(scene.overrides).length > 0);
          const hasAudio =
            scene.audioReady ??
            (Boolean(scene.narrationAudioUrl || scene.audioUrl) || scene.renderStatus === 'audio_ready');
          const isWarning = scene.validationStatus === 'warning';
          const hasNarration = Boolean((scene.narration || scene.text || '').trim());
          const hasImage = Boolean((scene.imageUrl || scene.imagePath || '').trim());

          return (
            <button
              key={scene.id}
              ref={isSelected ? activeItemRef : null}
              type="button"
              onClick={() => onSelectScene(scene.id)}
              className={`group relative flex-shrink-0 flex flex-col items-center p-2 rounded-xl transition-all duration-200 text-left border ${
                isSelected
                  ? 'border-brand-primary bg-brand-light/70 shadow-sm ring-2 ring-brand-primary/20 scale-[1.02]'
                  : 'border-bdr bg-white hover:border-gray-400 hover:bg-surface-secondary'
              }`}
              style={{ width: '136px' }}
            >
              {/* Thumbnail Container (16:9) */}
              <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-slate-100 border border-bdr flex items-center justify-center">
                {scene.imageUrl ? (
                  <img
                    src={formatMediaUrl(scene.imageUrl)}
                    alt={`Cena ${displayNum}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center text-slate-400 p-1">
                    <Monitor className="w-6 h-6 mb-0.5 text-slate-400 group-hover:text-brand-primary transition-colors" />
                    <span className="text-[10px] font-mono font-medium">Slide {displayNum}</span>
                  </div>
                )}

                {/* Overlays / Badges */}
                <div className="absolute top-1 left-1 flex gap-1">
                  {hasCustomOverrides && (
                    <span
                      title="Configurações personalizadas ativas"
                      className="w-2.5 h-2.5 rounded-full bg-brand-primary ring-2 ring-white"
                    />
                  )}
                </div>

                <div className="absolute top-1 right-1 flex gap-1">
                  {(!hasNarration || !hasImage) ? (
                    <span
                      title={
                        !hasNarration && !hasImage
                          ? 'Cena sem roteiro e sem imagem'
                          : !hasNarration
                          ? 'Cena sem roteiro'
                          : 'Cena sem imagem'
                      }
                      className="bg-amber-600 text-white rounded px-1 py-0.5 text-[9px] font-bold shadow-sm flex items-center gap-0.5"
                    >
                      <AlertCircle className="w-2.5 h-2.5" />
                      {!hasNarration && !hasImage ? 'Falta tudo' : !hasNarration ? 'Sem texto' : 'Sem imagem'}
                    </span>
                  ) : (
                    <>
                      {isWarning && (
                        <span title="Aviso de validação nesta cena" className="bg-amber-500 text-white rounded p-0.5 shadow-sm">
                          <AlertCircle className="w-3 h-3" />
                        </span>
                      )}
                      {hasAudio && (
                        <span title="Áudio pronto" className="bg-status-success text-white rounded p-0.5 shadow-sm">
                          <Volume2 className="w-3 h-3" />
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Title & Metadata */}
              <div className="w-full mt-2 flex items-center justify-between text-xs">
                <span
                  className={`font-semibold truncate ${
                    isSelected ? 'text-brand-primary font-bold' : 'text-txt-primary'
                  }`}
                >
                  Cena {String(displayNum).padStart(2, '0')}
                </span>

                {scene.duration ? (
                  <span className="text-[10px] text-txt-muted tabular-nums">
                    {Math.round(scene.duration)}s
                  </span>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
